import { useState } from "react";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  CardDescription,
} from "@/components/ui/card";
import {
  Table,
  TableHead,
  TableHeader,
  TableBody,
  TableRow,
  TableCell,
} from "@/components/ui/table";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { ButtonType } from "@/components/lib/Button";
import { Trash2, Plus } from "lucide-react";
import {
  removeItemVariant,
  setItemVariants,
} from "@/redux/slice/essential-menu-slice/variants-slice";
import FormInputField from "@/components/lib/from-input-field";
import CreateItem from "@/components/dialog/menu-manage/common-dialog/create-item";
import { useEffect } from "react";
import { useSelector, useDispatch } from "react-redux";
import { toast } from "react-hot-toast";
import {
  fetchSingleBuffet,
  updateExistingBuffet,
} from "@/redux/slice/menu-manage-slice/special-menu/buffets-slice";
import { Loader2 } from "lucide-react";

function Step2({ form, item_id }) {
  const [openDialog, setOpenDialog] = useState(false);
  const [showAdult, setShowAdult] = useState(false);
  const [showKids, setShowKids] = useState(false);
  const [showTime, setShowTime] = useState(false);
  const [loading, setLoading] = useState(false);
  const adult_price = form.watch("adult_price");
  const kids_price = form.watch("kids_price");
  const time = form.watch("time");
  const dispatch = useDispatch();
  const { itemVariants } = useSelector((state) => state.essentialMenu.variants);

  useEffect(() => {
    const fetchSingleItem = async () => {
      try {
        const res = await dispatch(fetchSingleBuffet(item_id));
        if (fetchSingleBuffet.fulfilled.match(res)) {
          const item = res?.payload?.buffet_items;
          dispatch(setItemVariants(item || []));
        }
      } catch (error) {
        console.log(error);
      }
    };
    fetchSingleItem();
  }, []);

  const handleItemUpdate = async (payload, actionType = "add") => {
    if (!payload) return;
    setLoading(true);
    let allItem = [...itemVariants];
    if (actionType === "add") {
      allItem = [...itemVariants, payload];
    } else if (actionType === "remove") {
      allItem = itemVariants.filter((item) => item.unique_id !== payload);
    }
    form.setValue("course_items", allItem);
    try {
      const res = await dispatch(
        updateExistingBuffet({
          id: item_id,
          data: {
            status: `${
              form.watch("type") === "update" ? form.watch("status") : "draft"
            }`,
            buffet_items: allItem,
          },
        })
      );
      if (updateExistingBuffet.fulfilled.match(res)) {
        // toast.success("Combo updated successfully.");
      } else {
        // toast.error(res?.payload || "Failed to update the combo.");
      }
    } catch (error) {
      toast.error("Failed to update the combo meal.");
         setLoading(false);
    }
    setLoading(false);
  };

  useEffect(() => {
    setShowAdult(!!adult_price);
    setShowKids(!!kids_price);
    setShowTime(!!time);
  }, [adult_price, kids_price, time]);

  useEffect(() => {
    const mappedItems = itemVariants?.map((item) => ({
      item_id: item.item_id,
      item_type: item.item_type,
      variant_id: item.variant_id,
      file_manager_id: item.file_manager_id,
      quantity: item.quantity,
      price: item.price ?? null,
    }));

    form.setValue("buffet_items", mappedItems);
  }, [itemVariants]);

  const handleRemove = async (id) => {
    await dispatch(removeItemVariant(id));
    await handleItemUpdate(id, "remove");

    toast.success("Buffet item removed successfully");
  };

  return (
    <Card className="max-w-screen-md bg-cblack6 mx-auto mt-10 text-cwhite">
      <CardHeader>
        <CardTitle>
          {" "}
          {form.watch("type") == "update"
            ? "Update Buffet"
            : "Add New Buffet"}{" "}
        </CardTitle>
        <CardDescription className="text-cyellow">
          Item name: {form.watch("name") || "No name"}
        </CardDescription>
      </CardHeader>
      <CardContent>
        {/* Items Section */}
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-lg font-semibold">Add Buffet Item</h2>
          <ButtonType
            onClick={() => setOpenDialog(true)}
            className="bg-cgreen/70 text-cwhite flex flex-shrink-0"
          >
            <span className="flex items-center">
              <Plus className="w-4 h-4 mr-1" size={18} /> Add New Item
            </span>
          </ButtonType>
        </div>
        <div className="relative">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Item Name</TableHead>
                <TableHead>Image</TableHead>
                <TableHead>Quantity</TableHead>
                <TableHead>Price</TableHead>
                <TableHead>In Price</TableHead>
                <TableHead>Out Price</TableHead>
                <TableHead className="text-right">Action</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {itemVariants && itemVariants.length > 0 ? (
                itemVariants.map((item, i) => (
                  <TableRow key={i}>
                    <TableCell>
                      <TableCell>
                        {item?.item_name ||
                          item?.menu_item?.name ||
                          "Data not available"}
                      </TableCell>
                    </TableCell>
                    <TableCell>
                      {item.file_manager_url ? (
                        <img
                          src={item.file_manager_url}
                          alt={item.item_name}
                          className="w-12 h-12 rounded"
                        />
                      ) : (
                        "No Image"
                      )}
                    </TableCell>
                    <TableCell>{item.quantity ?? "N/A"}</TableCell>
                    <TableCell>
                      {item.price !== undefined ? `£ ${item.price}` : "N/A"}
                    </TableCell>
                    <TableCell>
                      {item.in_price !== undefined
                        ? `£ ${item.in_price}`
                        : "N/A"}
                    </TableCell>
                    <TableCell>
                      {item.out_price !== undefined
                        ? `£ ${item.out_price}`
                        : "N/A"}
                    </TableCell>
                    <TableCell className="text-right">
                      <span className="flex items-center justify-end gap-3">
                        <Trash2
                          onClick={() => handleRemove(item?.unique_id)}
                          className="w-4 h-4 text-cred cursor-pointer"
                        />
                      </span>
                    </TableCell>
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell colSpan={7} className="text-center">
                    No data available
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>

          {loading && (
            <div className="absolute inset-0 bg-black/40 flex justify-center items-center z-10">
              <Loader2 className="animate-spin" size={18} />
            </div>
          )}
        </div>

        {/* Price */}
        <div className="flex flex-col gap-7 mt-5">
          <div className="">
            <div className="flex gap-2 items-center mb-3">
              <Switch
                checked={showAdult}
                onCheckedChange={(val) => {
                  setShowAdult(val);
                  if (!val) {
                    form.setValue("adult_price", null);
                  }
                }}
              />
              <Label>Adult Price</Label>
            </div>
            {showAdult && (
              <FormInputField
                name="adult_price"
                style="bg-cblack7"
                placeholder={"Adult pirce"}
                form={form}
                type="text"
              />
            )}
          </div>
          <div className="">
            <div className="flex gap-2 items-center mb-3">
              <Switch
                checked={showKids}
                onCheckedChange={(val) => {
                  setShowKids(val);
                  if (!val) {
                    form.setValue("kids_price", null);
                  }
                }}
              />
              <Label>Kids Price</Label>
            </div>
            {showKids && (
              <FormInputField
                name="kids_price"
                style="bg-cblack7"
                placeholder={"Kids pirce"}
                form={form}
                type="text"
              />
            )}
          </div>
          <div className="">
            <div className="flex gap-2 items-center mb-3">
              <Switch
                checked={showTime}
                onCheckedChange={(val) => {
                  setShowTime(val);
                  if (!val) {
                    form.setValue("time", null);
                  }
                }}
              />
              <Label>Time (Hours)</Label>
            </div>
            {showTime && (
              <FormInputField
                name="time"
                style="bg-cblack7"
                placeholder={"1"}
                form={form}
                type="text"
              />
            )}
          </div>
        </div>
      </CardContent>

      {/* Dialog */}
      <CreateItem
        title={"Add Buffet Item"}
        isOpen={openDialog}
        onClose={() => setOpenDialog(false)}
        onSuccess={(newItem) => handleItemUpdate(newItem, "add")}
      />
    </Card>
  );
}

export default Step2;
